'use client';

import { ArrowLeft } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

import { Logo } from '@ui/components/common/logo/Logo';
import { cn } from '@ui/lib/utils';
import { AuthActions } from '@website/components/AppHeader/AuthActions';
import { useBackNavigation } from '@website/hooks/useBackNavigation';
import { getLoginHrefClient } from '@website/utils/authClient';

interface SearchTopBarProps {
  hidden: boolean;
}

/**
 * Mobile top row for visitors: the app header is hidden on mobile and visitors
 * get no bottom nav, so without it the search page has no way out. Collapses
 * while the results scroll down; the filter bar below it stays in place.
 */
export function SearchTopBar({ hidden }: SearchTopBarProps) {
  const locale = useLocale();
  const t = useTranslations('Common');
  const handleBack = useBackNavigation(`/${locale}`);

  return (
    <div
      className={cn(
        'grid md:hidden transition-[grid-template-rows] duration-200 ease-out',
        hidden ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
      )}
      inert={hidden}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="layout-bounds flex items-center justify-between gap-2 py-2">
          <div className="flex items-center gap-1 min-w-0">
            <button
              type="button"
              aria-label={t('goBack')}
              onClick={handleBack}
              className="flex items-center justify-center -ml-2 size-10 text-neutral-900"
            >
              <ArrowLeft className="size-5" />
            </button>
            <Link href={`/${locale}`} aria-label="TopTopTime — home" className="h-8">
              <Logo variant="full" />
            </Link>
          </div>
          <AuthActions loginHref={getLoginHrefClient(locale)} />
        </div>
      </div>
    </div>
  );
}
